import React from 'react';
import { getBuyingSignalColor, MultiPartyCustomer, MultiPartyInsights, MultiPartyRep } from '../../adherence/types';
import { inferredRoleLabel, signalText } from '../multiPartyFormat';

/**
 * Who was on the call, at a glance, on the Summary tab: each rep's own numbers
 * and read, each customer's hedged role, buying signal and read. The same card
 * for a 1-to-1 call and a meeting; only the rows differ. The full view is the
 * Participants section of Details, one click away.
 */
interface ParticipantsSummaryCardProps {
  data: MultiPartyInsights;
  onViewDetails: () => void;
}

const MISSING = '—';
/** A 0-100 score as "72%", or a dash when the analysis has none. */
const percentOrMissing = (value?: number | null): string =>
  value === null || value === undefined ? MISSING : `${value}%`;

/** Handling is scored only over concerns the rep answered; none answered has no score. */
const handlingText = (rep: MultiPartyRep): string =>
  (rep?.concernsHandled ?? 0) > 0 && rep?.handlingScore !== null && rep?.handlingScore !== undefined
    ? `Handling ${rep.handlingScore}%`
    : 'No concerns handled';

const GroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-2">{children}</p>
);

/** A person's one-line read under their row, when the analysis has one. */
const ReadText: React.FC<{ parts: (string | null | undefined)[] }> = ({ parts }) => {
  const read = parts.filter(Boolean).join(' ');
  return read ? <p className="mt-0.5 text-xs text-ink-muted leading-relaxed">{read}</p> : null;
};

const RepRow: React.FC<{ rep: MultiPartyRep }> = ({ rep }) => (
  <li data-testid={`summary-rep-${rep?.participantId}`}>
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="font-semibold text-ink truncate">{rep?.name}</span>
      <span className="text-xs text-ink-secondary whitespace-nowrap">
        Talk {percentOrMissing(rep?.talkPct)} · Confidence {percentOrMissing(rep?.tone?.confidence)} ·{' '}
        {handlingText(rep)}
      </span>
    </div>
    <ReadText parts={[rep?.behaviour, rep?.playbookContribution]} />
  </li>
);

const CustomerRow: React.FC<{ customer: MultiPartyCustomer }> = ({ customer }) => (
  <li data-testid={`summary-customer-${customer?.participantId}`}>
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="font-semibold text-ink truncate">{customer?.name}</span>
      <span className="flex items-center gap-2 text-xs text-ink-secondary whitespace-nowrap">
        {inferredRoleLabel(customer?.inferredRole)}
        {customer?.buyingSignal && (
          <span className={`px-2 py-0.5 rounded capitalize ${getBuyingSignalColor(customer.buyingSignal)}`}>
            {signalText(customer.buyingSignal)}
          </span>
        )}
      </span>
    </div>
    <ReadText parts={[customer?.roleEvidence, customer?.behaviour]} />
  </li>
);

const ParticipantsSummaryCard: React.FC<ParticipantsSummaryCardProps> = ({ data, onViewDetails }) => {
  const reps = data?.reps ?? [];
  const customers = data?.customers ?? [];
  return (
    <section className="bg-white border border-hairline rounded-xl shadow-md overflow-hidden">
      <div className="px-4 py-3 bg-surface-subtle border-b border-hairline">
        <h3 className="text-sm font-bold text-ink uppercase tracking-wide">Participants</h3>
      </div>
      <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {reps.length > 0 && (
          <div>
            <GroupTitle>Salespeople</GroupTitle>
            <ul className="space-y-3">
              {reps.map((rep) => (
                <RepRow key={rep?.participantId} rep={rep} />
              ))}
            </ul>
          </div>
        )}
        {customers.length > 0 && (
          <div>
            <GroupTitle>Customers</GroupTitle>
            <ul className="space-y-3">
              {customers.map((customer) => (
                <CustomerRow key={customer?.participantId} customer={customer} />
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-hairline bg-surface-subtle">
        <button
          type="button"
          onClick={onViewDetails}
          className="text-xs text-signature hover:text-signature-strong font-medium underline"
        >
          View all participants &rarr;
        </button>
      </div>
    </section>
  );
};

export default ParticipantsSummaryCard;
